import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';
import { getServerErrorMessage } from '@src/helpers/serverErrors';
import { EMPTY_EVALUATION_RESULT, EvaluationResult, readEvaluationResult } from './types';

const RESOURCE = 'serviceowner_ai_processors';

/** the model is rate limited, so a run takes seconds to minutes — this is not a tick */
export const POLL_INTERVAL_MS = 4000;

export const PERMISSION_NOTICE =
    'You do not have permission to run this evaluation. Only an admin or configuration member of the owning team can.';

export type UseEvaluationRun = {
    /** the latest run, always an object — never null, so nothing has to guard it */
    result: EvaluationResult;
    /** the first read of the detail endpoint has not come back yet */
    loading: boolean;
    /** the POST is in flight */
    starting: boolean;
    /** a run is in progress and being polled for */
    polling: boolean;
    /** why the run cannot be read or started, when that is the answer */
    notice: string | null;
    run: () => void;
};

/**
 * The evaluation of one AI processor: what the last run said, and starting the
 * next one.
 *
 * `evaluation_result` lives on the **detail** endpoint only — the list reports
 * it as null — and react-admin hands a form whatever the store already holds,
 * which for a form reached from its list is the list's record. So this reads
 * the processor itself rather than taking the result off the record it was
 * given, which also makes the poll and the first read the same call.
 *
 * A run is asynchronous: the 202 only means the rows were queued. The aggregate
 * moves out of `WAITING_FOR_RESPONSE` on its own and stops at `OK` or `FAILED`,
 * so those two states are where the polling stops. It also stops on unmount,
 * and on a read that fails — a failing read repeated every four seconds is
 * still a failing read.
 */
const useEvaluationRun = (processorId?: number | string): UseEvaluationRun => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();

    const [result, setResult] = useState<EvaluationResult>(EMPTY_EVALUATION_RESULT);
    const [loading, setLoading] = useState<boolean>(!!processorId);
    const [starting, setStarting] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    /** bumped to restart the read loop — after a run is accepted, or already going */
    const [round, setRound] = useState(0);

    const live = useRef(true);
    useEffect(() => {
        live.current = true;
        return () => {
            live.current = false;
        };
    }, []);

    /* The loop below restarts whenever its dependencies change, and restarting
       it means another read — so the provider must not be one of them. Holding
       it in a ref says that outright rather than resting on the caller handing
       back the same object on every render. */
    const provider = useRef(dataProvider);
    provider.current = dataProvider;

    useEffect(() => {
        if (!processorId) return undefined;

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const read = () => {
            provider.current
                .getOne(RESOURCE, { id: processorId })
                .then(({ data }: any) => {
                    if (cancelled) return;
                    const next = readEvaluationResult(data);
                    setResult(next);
                    setLoading(false);
                    if (next.state === 'WAITING_FOR_RESPONSE') timer = setTimeout(read, POLL_INTERVAL_MS);
                })
                .catch((error: any) => {
                    if (cancelled) return;
                    setLoading(false);
                    if (error?.status === 403 || error?.status === 404) setNotice(PERMISSION_NOTICE);
                });
        };

        read();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [processorId, round]);

    const run = useCallback(() => {
        if (!processorId) return;
        setStarting(true);
        setNotice(null);

        const done = () => {
            if (live.current) setStarting(false);
        };

        provider.current
            .createAction(RESOURCE, { id: processorId, verb: 'evaluate/', data: undefined })
            .then(() => {
                done();
                if (!live.current) return;
                notify('Evaluation started');
                setRound((previous) => previous + 1);
            })
            .catch((error: any) => {
                done();
                if (!live.current) return;

                // a run of our own was already going: nothing failed, and what
                // the page wants next is the run it already has
                if (error?.status === 409) {
                    notify('An evaluation run is already in progress.', 'warning');
                    setRound((previous) => previous + 1);
                    return;
                }

                if (error?.status === 403 || error?.status === 404) {
                    setNotice(PERMISSION_NOTICE);
                    return;
                }

                // a 400 arrives as a bare list of sentences — no cases, not
                // active, wrong action type — each of which names its own fix
                notify(getServerErrorMessage(error, 'Could not start the evaluation'), 'error');
            });
    }, [processorId, notify]);

    return {
        result,
        loading,
        starting,
        polling: result.state === 'WAITING_FOR_RESPONSE',
        notice,
        run,
    };
};

export default useEvaluationRun;
